import { AnalyticsEventNameType } from "@coeff/api";
import React, { ReactElement, useCallback, useEffect, useRef, useState } from "react";

import {
  ChartIcon,
  HeaderIcon,
  ImageIcon,
  PlusIcon,
  SpeedometerIcon,
  TextIcon,
} from "../../../components/Icons";
import { Tooltip, TooltipDirection } from "../../../components/Tooltip/index";
import { useTrack } from "../../../utils";
import { DashboardWindow } from "../dashboardHelpers";
import { useWebDashboardViewerContext } from "../WebDashboardViewerContext";

import { AddAiPromptForm } from "./AddAiPromptForm";
import { AddSectionHeaderForm } from "./AddSectionHeaderForm";
import { AddTextBlockForm } from "./AddTextBlockForm";

export type AddBlockType = "chart" | "metric_card" | "text_block" | "ai_image" | "section_header";

interface AddBlockMenuItem {
  type: AddBlockType;
  label: string;
  icon: ReactElement;
  usesAiPrompt: boolean;
}

const BLOCK_MENU_ITEMS: AddBlockMenuItem[] = [
  { type: "chart", label: "Chart", icon: <ChartIcon />, usesAiPrompt: true },
  { type: "metric_card", label: "KPI", icon: <SpeedometerIcon />, usesAiPrompt: true },
  { type: "text_block", label: "Text block", icon: <TextIcon />, usesAiPrompt: false },
  { type: "ai_image", label: "AI Image", icon: <ImageIcon />, usesAiPrompt: true },
  { type: "section_header", label: "Section header", icon: <HeaderIcon />, usesAiPrompt: false },
];

const ADD_BLOCK_OPEN_EVENT = "web-dashboard-add-block-open";

export type AddBlockFlow =
  | { type: "section_header" }
  | { type: "text_block" }
  | { type: "ai_prompt"; blockType: AddBlockType }
  | null;

interface Props {
  onAddBlock: (blockType: AddBlockType) => void;
  onAddBlockWithPrompt?: (blockType: AddBlockType, prompt: string) => void;
  onOpenStateChange?: (isOpen: boolean) => void;
  targetWindow: DashboardWindow;
  direction: "horizontal" | "vertical";
}

export const AddBlockButton: React.FC<Props> = ({
  onAddBlock,
  onAddBlockWithPrompt,
  onOpenStateChange,
  targetWindow,
  direction,
}) => {
  const track = useTrack();
  const { isAiThinking, dashboardId, dashboardName, spreadsheetId } =
    useWebDashboardViewerContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [flow, setFlow] = useState<AddBlockFlow>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const instanceIdRef = useRef(`add-block-${Math.random().toString(36).slice(2, 10)}`);

  const closeAll = useCallback(() => {
    setIsMenuOpen(false);
    setFlow(null);
  }, []);

  useEffect(() => {
    if (!isMenuOpen && !flow) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        closeAll();
      }
    };
    targetWindow.document.addEventListener("mousedown", handleClickOutside);
    return () => targetWindow.document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen, flow, targetWindow, closeAll]);

  useEffect(() => {
    if (isAiThinking) {
      closeAll();
    }
  }, [isAiThinking, closeAll]);

  useEffect(() => {
    const handler = (event: Event) => {
      const e = event as CustomEvent<{ sourceId?: string }>;
      if (e.detail?.sourceId === instanceIdRef.current) {
        return;
      }
      closeAll();
    };
    targetWindow.document.addEventListener(ADD_BLOCK_OPEN_EVENT, handler);
    return () => targetWindow.document.removeEventListener(ADD_BLOCK_OPEN_EVENT, handler);
  }, [targetWindow, closeAll]);

  const isHorizontal = direction === "horizontal";
  const hasInlineFlow = flow !== null;
  const isOpen = isMenuOpen || hasInlineFlow;

  useEffect(() => {
    onOpenStateChange?.(isOpen);
  }, [isOpen, onOpenStateChange]);

  const trackBlockAdded = useCallback(
    (blockType: AddBlockType, blockTitle?: string) => {
      track(AnalyticsEventNameType.VibeDashBlockAdded, {
        vibe_dash_id: dashboardId,
        ss_id: spreadsheetId,
        vibe_dash_name: dashboardName,
        vibe_dash_block_type: blockType,
        ...(blockTitle ? { vibe_dash_block_title: blockTitle } : {}),
      });
    },
    [track, dashboardId, spreadsheetId, dashboardName]
  );

  const handleItemClick = useCallback(
    (item: AddBlockMenuItem) => {
      setIsMenuOpen(false);

      switch (item.type) {
        case "section_header":
          setFlow({ type: "section_header" });
          break;
        case "text_block":
          setFlow({ type: "text_block" });
          break;
        case "chart":
        case "metric_card":
        case "ai_image":
          if (item.usesAiPrompt && onAddBlockWithPrompt) {
            setFlow({ type: "ai_prompt", blockType: item.type });
          } else {
            trackBlockAdded(item.type);
            onAddBlock(item.type);
          }
          break;
        default:
          trackBlockAdded(item.type);
          onAddBlock(item.type);
      }
    },
    [onAddBlock, onAddBlockWithPrompt, trackBlockAdded]
  );

  const handleFlowDone = useCallback(
    (blockType: AddBlockType, payload: string) => {
      trackBlockAdded(blockType, payload);
      if (onAddBlockWithPrompt) {
        onAddBlockWithPrompt(blockType, payload);
      } else {
        onAddBlock(blockType);
      }
      setFlow(null);
    },
    [onAddBlock, onAddBlockWithPrompt, trackBlockAdded]
  );

  const renderInlineForm = () => {
    if (!flow) {
      return null;
    }

    switch (flow.type) {
      case "section_header":
        return (
          <AddSectionHeaderForm
            menuRef={menuRef}
            isHorizontal={isHorizontal}
            onDone={payload => handleFlowDone("section_header", payload)}
            onClose={closeAll}
          />
        );
      case "text_block":
        return (
          <AddTextBlockForm
            menuRef={menuRef}
            isHorizontal={isHorizontal}
            onDone={markdown => handleFlowDone("text_block", markdown)}
            onClose={closeAll}
          />
        );
      case "ai_prompt":
        return (
          <AddAiPromptForm
            blockType={flow.blockType}
            menuRef={menuRef}
            isHorizontal={isHorizontal}
            onDone={payload => handleFlowDone(flow.blockType, payload)}
            onClose={closeAll}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="relative flex items-center justify-center mx-2">
      <button
        ref={buttonRef}
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
          if (isAiThinking) {
            return;
          }
          if (hasInlineFlow) {
            closeAll();
          } else {
            const nextOpen = !isMenuOpen;
            if (nextOpen) {
              targetWindow.document.dispatchEvent(
                new CustomEvent(ADD_BLOCK_OPEN_EVENT, {
                  detail: { sourceId: instanceIdRef.current },
                })
              );
              track(AnalyticsEventNameType.VibeDashAddBlockMenuOpened, {
                vibe_dash_id: dashboardId,
                ss_id: spreadsheetId,
                vibe_dash_name: dashboardName,
              });
            }
            setIsMenuOpen(nextOpen);
          }
        }}
        onMouseDown={e => e.stopPropagation()}
        disabled={isAiThinking}
        aria-label="Add new block"
        className={`themed-icon-button !w-5 !h-5 !min-w-0 !min-h-0 transition-all duration-150 border-none text-main-muted-foreground hover:text-[#3053FF]
          ${isAiThinking ? "opacity-50 cursor-not-allowed" : ""}
        `}
        onMouseOver={e => {
          e.preventDefault();
          e.stopPropagation();
          setIsTooltipVisible(true);
        }}
        onMouseLeave={e => {
          e.preventDefault();
          e.stopPropagation();
          setIsTooltipVisible(false);
        }}
      >
        <PlusIcon className="" />
        <Tooltip showTooltip={isTooltipVisible} direction={TooltipDirection.Down}>
          Add new...
        </Tooltip>
      </button>

      {isMenuOpen && (
        <div
          ref={menuRef}
          className={`absolute z-50 bg-white rounded-lg shadow-xl border border-gray-200 pt-2 pr-4 pb-3 pl-2 w-max ${
            isHorizontal ? "top-5" : "left-5"
          }`}
        >
          <div className="px-3 py-1.5 text-lg font-bold text-gray-400">Add new...</div>
          {BLOCK_MENU_ITEMS.map(item => (
            <button
              key={item.type}
              onClick={() => handleItemClick(item)}
              className="w-full px-3 py-2 text-left text-lg font-semibold flex items-center gap-2.5 text-black hover:bg-gray-50 cursor-pointer transition-colors"
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      )}

      {renderInlineForm()}
    </div>
  );
};
